import { NavLink } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Can from '../Can'
import NavigationLabel from '../NavigationLabel/NavigationLabel'
import { selectUserData } from '../../reducers/userDataReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { NAVIGATION_LABELS } from '../../helpers/constants'
import { useUserPermissions } from '../../hooks/useUserPermissions'

import ChecklistsLink from './UpsellingLinks/ChecklistsLink'
import AmenityReservationsLink from './UpsellingLinks/AmenityReservationsLink'
import MaintenanceRequestLink from './UpsellingLinks/MaintenanceRequestLink'
import VisitorManagementLink from './UpsellingLinks/VisitorManagementLink'
import PackageManagementLink from './UpsellingLinks/PackageManagementLink'
import MarketShopLink from './UpsellingLinks/MarketShopLink'
import BuildingEssentialsLink from './UpsellingLinks/BuildingEssentialsLink'
import ParkingLink from './UpsellingLinks/ParkingLink'
import ParkingManagementLink from './UpsellingLinks/ParkingManagementLink'
import InternalInspectionsLink from './UpsellingLinks/InternalInspectionsLink'
import IncidentReportsLink from './UpsellingLinks/IncidentReportsLink'
import AssetTrackingLink from './UpsellingLinks/AssetTrackingLink'
import FrontDeskLink from './UpsellingLinks/FrontDeskLink'
import RestaurantManagerLink from './UpsellingLinks/RestaurantManagerLink'

const OperationsModal = ({ userRole, buildingData }) => {
  const { t } = useTranslation(['navbar'])
  const isLinkHiddenForRole = ['Staff', 'ShiftManager'].includes(userRole)
  const isBuilding724 = buildingData?.id === 724 || buildingData?.buildingId === 724
  const isBuilding638 = buildingData?.id === 638 || buildingData?.buildingId === 638
  const isSpringlineProperty =
    [894, 927, 928].includes(buildingData?.id) || [894, 927, 928].includes(buildingData?.buildingId)

  const { useNewPermissions, hasPermission } = useUserPermissions()

  return (
    <div className="navmgr-hover-menu-operations">
      <div className="hover-menu-arrow" />
      <div className="navmgr-hover-menu__inner">
        {isBuilding638 && (
          <Can on={'Navbar'} to={'instructions'}>
            <div className="hover-menu-item">
              <NavLink
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
                }
                to="/dashboard/building-dashboard/instructions"
              >
                <span>
                  {t('navbar:operationsModal.instructions')}
                  <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            </div>
          </Can>
        )}
        <Can
          on={'Navbar'}
          to={'checklists'}
          feedFlag={'checklistsAvailable'}
          upsellingLink={<ChecklistsLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <ChecklistsLink />
        </Can>
        <Can on={'Navbar'} to={'managementUsers'}>
          <div className="hover-menu-item">
            <NavLink
              className={({ isActive }) =>
                isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
              }
              to="/dashboard/building-dashboard/management-users"
            >
              <span>
                {t('navbar:operationsModal.users')}
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
            </NavLink>
          </div>
        </Can>
        <Can
          on={'Navbar'}
          to={'reservations'}
          feedFlag={'amenityReservations'}
          upsellingLink={<AmenityReservationsLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <AmenityReservationsLink />
        </Can>

        {useNewPermissions ? (
          <>{hasPermission('maintenance') && <MaintenanceRequestLink />}</>
        ) : (
          <Can
            on={'Navbar'}
            to={'maintenance'}
            feedFlag={'maintenanceRequestAvailable'}
            upsellingLink={<MaintenanceRequestLink withUpsellingRedirect />}
          >
            <MaintenanceRequestLink />
          </Can>
        )}

        {isBuilding724 ? (
          <VisitorManagementLink />
        ) : (
          <Can
            on={'Navbar'}
            to={'visitors'}
            feedFlag={'guestAccess'}
            upsellingLink={<VisitorManagementLink withUpsellingRedirect />}
            forceHideChildren={isLinkHiddenForRole}
          >
            <VisitorManagementLink />
          </Can>
        )}
        <Can
          on={'Navbar'}
          to={'packages'}
          feedFlag={'packagesAvailable'}
          upsellingLink={<PackageManagementLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <PackageManagementLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'marketShop'}
          feedFlag={'marketShopAvailable'}
          forceHideChildren={isLinkHiddenForRole}
          upsellingLink={<MarketShopLink withUpsellingRedirect />}
        >
          <MarketShopLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'internalInspections'}
          feedFlag={'inspectionsAvailable'}
          upsellingLink={<InternalInspectionsLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <InternalInspectionsLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'incidentReports'}
          feedFlag={'incidentReportsAvailable'}
          upsellingLink={<IncidentReportsLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <IncidentReportsLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'assetTracking'}
          feedFlag={'assetTrackingAvailable'}
          upsellingLink={<AssetTrackingLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <AssetTrackingLink />
        </Can>
        {isSpringlineProperty && (
          <Can on={'Navbar'} to={'workOrdersOffice'}>
            <div className="hover-menu-item">
              <NavLink
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
                }
                to="/dashboard/building-dashboard/work-orders-office"
              >
                <span>{t('navbar:operationsModal.workOrdersOffice')}</span>
              </NavLink>
            </div>
          </Can>
        )}
        <Can
          on={'Navbar'}
          to={'frontDesk'}
          feedFlag={'frontDeskAvailable'}
          upsellingLink={<FrontDeskLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <FrontDeskLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'restaurantManager'}
          feedFlag={'restaurantManagerAvailable'}
          upsellingLink={<RestaurantManagerLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <RestaurantManagerLink />
        </Can>
        <Can on={'Navbar'} to={'actionLog'} feedFlag={'actionLogAvailable'}>
          <div className="hover-menu-item">
            <NavLink
              className={({ isActive }) =>
                isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
              }
              to="/dashboard/building-dashboard/action-log"
            >
              <span>
                {t('navbar:operationsModal.shiftAndActionLog')}
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
            </NavLink>
          </div>
        </Can>
        <Can on={'Navbar'} to={'booking'} feedFlag={'servicesAvailable'}>
          <div className="hover-menu-item">
            <NavLink
              className={({ isActive }) =>
                isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
              }
              to="/dashboard/booking"
            >
              <span>
                {t('navbar:operationsModal.bookAService')}
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
            </NavLink>
          </div>
        </Can>
        <Can
          on={'Navbar'}
          to={'parking'}
          feedFlag={'valetAvailable'}
          upsellingLink={<ParkingLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <ParkingLink />
        </Can>
        <Can
          on={'Navbar'}
          feedFlag={['parkingManagementAvailable', 'visitorParkingAvailable']}
          to={'parkingManagement'}
          upsellingLink={<ParkingManagementLink withUpsellingRedirect />}
          isAny
        >
          <ParkingManagementLink />
        </Can>
        <Can
          on={'Navbar'}
          to={'buildingEssentials'}
          feedFlag={'buildingEssentialsAvailable'}
          upsellingLink={<BuildingEssentialsLink withUpsellingRedirect />}
          forceHideChildren={isLinkHiddenForRole}
        >
          <BuildingEssentialsLink />
        </Can>
        <Can on={'Navbar'} to={'neighbourPerks'}>
          <div className="hover-menu-item">
            <NavLink
              className={({ isActive }) =>
                isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
              }
              to="/dashboard/building-dashboard/neighborhood-perks"
            >
              <span>
                {t('navbar:operationsModal.neighborhoodPerks')}
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
            </NavLink>
          </div>
        </Can>
        <Can
          on={'Navbar'}
          to={'payments'}
          isAny
          feedFlag={['yardiPaymentId', 'rentManagerPropertyId', 'payRentLink']}
        >
          <div className="hover-menu-item">
            {buildingData && buildingData.payRentLink ? (
              <a
                className="hover-menu-item__link"
                target="_blank"
                rel="noreferrer"
                href={buildingData.payRentLink}
              >
                <span>{t('navbar:operationsModal.payRent')}</span>
              </a>
            ) : (
              <NavLink
                to="/dashboard/payments"
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'hover-menu-item__link'
                }
              >
                <span>
                  {t('navbar:operationsModal.payRent')}
                  <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            )}
          </div>
        </Can>
      </div>
    </div>
  )
}

OperationsModal.propTypes = {
  userRole: PropTypes.string,
  buildingData: PropTypes.object,
}

export default connect((state) => {
  const userData = selectUserData(state)
  return {
    buildingData: selectActiveBuilding(state) || userData.buildingId,
  }
})(OperationsModal)
